import { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Box, Button, Flex, SimpleGrid, VStack } from '@chakra-ui/react';
import { FaRegBuilding } from 'react-icons/fa';
import { FiChevronRight, FiUser } from 'react-icons/fi';
import { AccountAddressRec, AddressRec, accountApi } from '@netiva/classifieds-api';
import { Loader, UserIcon } from '@netiva/classifieds-ui';

import { routes } from '@/lib/routes';
import { useAppSelector } from '@/store';
import { AddressFormVariant } from '../Addresses/components/AddressForm';
import { EmptyAddressData } from '../Addresses/components';
import { NewAccountCard, NewAccountForm, NewAccountHeader } from './components';

/** Keeps what the account address already holds and fills its gaps from the login. */
const completeAddress = (
  { accountSettings, ...address }: AccountAddressRec,
  loginAddress: AddressRec | undefined
): AddressRec => ({
  ...address,
  firstname: address.firstname || loginAddress?.firstname,
  lastname: address.lastname || loginAddress?.lastname,
  email: address.email || loginAddress?.email,
  street: address.street || loginAddress?.street || '',
  zip: address.zip || loginAddress?.zip || '',
  city: address.city || loginAddress?.city || '',
  countryIso2: address.countryIso2 || loginAddress?.countryIso2 || '',
});

export const NewAccountPage: FC = memo(function NewAccountPage() {
  const { t } = useTranslation();
  const { account, isLoaded: isAccountLoaded, hasValidAccount } = useAppSelector((state) => state.account);
  const [getLoginInfo, { isLoading: isLoadingLoginInfo }] = accountApi.useGetLoginInfoLazy();
  const [address, setAddress] = useState<AddressRec>();
  const [showForm, setShowForm] = useState(false);
  const [accountType, setAccountType] = useState<AddressFormVariant>('private');

  // an imported account already knows its type
  const existingAccountType = account?.accountType;
  useEffect(() => {
    if (existingAccountType === 'Company') {
      setAccountType('company');
    }
  }, [existingAccountType]);

  const handleContinue = async () => {
    let loginAddress: AddressRec | undefined;
    try {
      const loginInfo = await getLoginInfo({}).unwrap();
      if (loginInfo) {
        loginAddress = {
          ...EmptyAddressData,
          type: 'Account',
          city: loginInfo.city || '',
          countryIso2: loginInfo.countryCode || '',
          email: loginInfo.email,
          firstname: loginInfo.firstName,
          gender: 'Unknown',
          lastname: loginInfo.lastName,
          street: loginInfo.streetAddress || '',
          zip: loginInfo.zipCode || '',
        };
      }
    } catch (err) {
      console.error(err);
    }
    // an account can exist with an incomplete address (e.g. an imported one), which is completed instead of replaced
    setAddress(account?.address ? completeAddress(account.address, loginAddress) : loginAddress);
    setShowForm(true);
  };

  if (isAccountLoaded && hasValidAccount) {
    return <Navigate to={routes.home()} />;
  }

  return (
    <>
      <Loader isLoading={!isAccountLoaded} showOverlay />

      <Box flex="1" padding={{ base: 4, md: 8 }} bgColor="neutral.50">
        {showForm ? (
          <NewAccountForm type={accountType} address={address} onCancel={() => setShowForm(false)} />
        ) : (
          <VStack spacing={8} maxWidth="2xl" marginX="auto" textAlign="center">
            <NewAccountHeader
              icon={<UserIcon boxSize={6} />}
              title={t('new.account.setup.title')}
              description={t('new.account.setup.description')}
            />

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="full">
              <NewAccountCard
                icon={FiUser}
                title={t('new.account.type.private.title')}
                description={t('new.account.type.private.description')}
                isSelected={accountType === 'private'}
                onClick={() => setAccountType('private')}
              />
              <NewAccountCard
                icon={FaRegBuilding}
                title={t('new.account.type.company.title')}
                description={t('new.account.type.company.description')}
                isSelected={accountType === 'company'}
                onClick={() => setAccountType('company')}
              />
            </SimpleGrid>

            <Flex justify="center">
              <Button
                type="button"
                colorScheme="primary"
                rightIcon={<FiChevronRight />}
                onClick={handleContinue}
                isLoading={isLoadingLoginInfo}
                isDisabled={isLoadingLoginInfo}
              >
                {t('new.account.continue')}
              </Button>
            </Flex>
          </VStack>
        )}
      </Box>
    </>
  );
});
